'use client';

import * as React from 'react';
import { styled } from '@linaria/react';
import useSoundBase from 'use-sound';

import usePrefersReducedMotion from '@/hooks/use-prefers-reduced-motion';

import VisuallyHidden from '@/components/VisuallyHidden';

import { TINKERSYNTH_BULGE_BREAKPOINT } from '../AboutJosh.constants';
import BaseChild from './BaseChild';

function CardPronunciationGuide(
  delegated: React.HTMLAttributes<HTMLDivElement>
) {
  const [playPreRecordedSound, { stop }] = useSoundBase(
    '/sounds/comeau.mp3'
  );

  // NOTE: Keeping this commented-out code in case I someday want to reconstruct `comeau.mp3` or do it in realtime.
  // const [utterance] = React.useState(() => {
  //   if (typeof window === 'undefined') {
  //     return null;
  //   }

  //   return new SpeechSynthesisUtterance('Comb oh');
  // });
  const [startAt, setStartAt] = React.useState(0);

  return (
    <Wrapper
      {...delegated}
      onClick={() => {
        stop();
        playPreRecordedSound();
        // const voices = speechSynthesis
        //   .getVoices()
        //   .filter((voice) => voice.lang === 'en-US');

        // if (!utterance) {
        //   return;
        // }

        // utterance.voice = voices[3];

        // speechSynthesis.speak(utterance);

        setStartAt(Date.now());
      }}
    >
      <Phonetics>
        <Pronunciation aria-hidden="true">
          kəʊm əʊ <VolumeIcon key={startAt} startAt={startAt} />
        </Pronunciation>
        <VisuallyHidden>
          Phonetic spelling of my last name, Comeau. Triggering this
          button plays a recording of the pronunciation.
        </VisuallyHidden>
      </Phonetics>
      <p>
        My last name, Comeau, is pronounced “<em>cohm-oh</em>”.
      </p>
      <p>Click to hear a robot say it.</p>
    </Wrapper>
  );
}

function VolumeIcon({
  startAt,
  size = 20,
}: {
  startAt: number;
  size?: number;
}) {
  const prefersReducedMotion = usePrefersReducedMotion();

  return (
    <Svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path
        d="M12.0133 4.60757C11.9149 4.2957 11.5463 4.19331 11.2861 4.39151L5.93425 8.46914C5.84716 8.5355 5.74071 8.57143 5.63123 8.57143H1.5C1.22386 8.57143 1 8.79529 1 9.07143V14.9286C1 15.2047 1.22386 15.4286 1.5 15.4286H5.63123C5.74071 15.4286 5.84716 15.4645 5.93425 15.5309L11.2861 19.6085C11.5463 19.8067 11.9149 19.7043 12.0133 19.3924C12.3582 18.2999 13 15.7612 13 12C13 8.2388 12.3582 5.70014 12.0133 4.60757Z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      <InnerWave
        data-enable-animation={!prefersReducedMotion && startAt !== 0}
        d={`
          M 17.54 8.46002
          C 18.4774 9.39766 19.004 10.6692 19.004 11.995
          C 19.004 13.3208 18.4774 14.5924 17.54 15.53
        `}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <OuterWave
        data-enable-animation={!prefersReducedMotion && startAt !== 0}
        d={`
          M 20.0703 4.92999
          C 21.945 6.80527 22.9982 9.34835 22.9982 12
          C 22.9982 14.6516 21.945 17.1947 20.0703 19.07
        `}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  );
}

const Wrapper = styled(BaseChild)`
  /* On larger screen sizes, the TinkersynthMachine bulges up into this card, so we need more minimum space at the bottom */
  padding-bottom: 32px;
  container-type: inline-size;

  &:has(button:focus-visible) {
    outline: 2px solid var(--color-primary);
  }

  @media (max-width: ${TINKERSYNTH_BULGE_BREAKPOINT}rem) {
    padding-bottom: 16px;
  }
`;

const Phonetics = styled.button`
  margin: 0 auto 8px;
  height: 2.75rem;
  /* Optical alignment */
  transform: translateX(3px);
  -webkit-tap-highlight-color: transparent;

  @supports selector(:has(*)) {
    &:focus-visible {
      outline: none;
    }
  }

  @container (max-width: 11.125rem) {
    height: 2.25rem;
  }
`;

const Pronunciation = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  font-size: 2.5rem;
  text-align: center;
  font-family: math, sans-serif;
  white-space: nowrap;

  @container (max-width: 11.125rem) {
    font-size: 1.875rem;
  }
`;

const Svg = styled.svg`
  display: block;
  overflow: visible;
  color: var(--color-gray-700);
  transition: color 500ms;

  ${Wrapper}:hover &, ${Wrapper}:focus-visible & {
    color: var(--color-primary);
    transition: color 150ms;
  }
`;

const InnerWave = styled.path`
  &[data-enable-animation='true'] {
    animation: innerWave 700ms linear;

    @keyframes innerWave {
      0% {
        opacity: 0;
        transform: translateX(-2px);
      }
      13% {
        opacity: 0;
        transform: translateX(-2px);
      }
      18% {
        opacity: 1;
        transform: translateX(2px);
      }
      43% {
        opacity: 0;
        transform: translateX(-2px);
      }
      48% {
        opacity: 1;
        transform: translateX(2px);
      }

      70% {
        transform: translateX(0px);
      }
    }
  }
`;
const OuterWave = styled.path`
  &[data-enable-animation='true'] {
    animation: outerWave 700ms linear;

    @keyframes outerWave {
      0% {
        opacity: 0;
        transform: translateX(-3px);
      }
      17% {
        opacity: 0;
        transform: translateX(-3px);
      }
      22% {
        opacity: 1;
        transform: translateX(3px);
      }
      47% {
        opacity: 0;
        transform: translateX(-3px);
      }
      54% {
        opacity: 1;
        transform: translateX(3px);
      }

      75% {
        transform: translateX(0px);
      }
    }
  }
`;

export default CardPronunciationGuide;
